import { Collapse, Input } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { Link } from 'react-router-dom'
import Section from '../common/Section'
import { Table } from 'ant-table-extensions'
import _ from 'lodash'
import { compareStrings } from '../../util/compareUtil'
import { defaultDateTime } from '../../util/dateTimeUtil'
import useQueryParams from '../../hooks/useQueryParams'
import { useRef } from 'react'
import useRegistrations from '../../hooks/api/useRegistrations'

const flatten = (data) => _.flatMap(data, (data) => ({ id: data.id, ...data.attributes }))

const colProps = (dataIndex) => {
  return {
    sorter: (a, b) => compareStrings(_.get(a, dataIndex), _.get(b, dataIndex)),
  }
}

const RegistrationListPage = () => {
  const { registrations, loading, error } = useRegistrations()

  const data = useMemo(() => flatten(registrations), [registrations])
  const query = useQueryParams()
  const initialSearch = query.get('search') || ''

  const tableWrapperRef = useRef(null)
  const [filteredData, setFilteredData] = useState([])

  useEffect(() => {
    const s = (initialSearch || '').trim().toLowerCase()
    if (!s) {
      setFilteredData(data)
    } else {
      setFilteredData(
        _.filter(data, (r) => {
          try {
            return JSON.stringify(r).toLowerCase().includes(s)
          } catch (e) {
            return false
          }
        }),
      )
    }
  }, [data, initialSearch])

  // If a `search` query param was provided, try to prefill the built-in table search input.
  useEffect(() => {
    if (!initialSearch) return
    // wait until DOM mounts
    const t = setTimeout(() => {
      try {
        const root = tableWrapperRef.current
        if (!root) return
        // look for an input inside the table controls
        const input = root.querySelector('input')
        if (!input) return
        input.focus()
        input.value = initialSearch
        // dispatch input/change events so the table's listeners pick it up
        const ev = new Event('input', { bubbles: true })
        input.dispatchEvent(ev)
        const ev2 = new Event('change', { bubbles: true })
        input.dispatchEvent(ev2)
      } catch (e) {
        // noop
      }
    }, 50)
    return () => clearTimeout(t)
  }, [initialSearch])

  const stateFilters = useMemo(
    () => _.uniq(_.map(data, 'state')).map((state) => ({ text: state, value: state })),
    [data],
  )
  const membershipTypeFilters = useMemo(
    () => _.uniq(_.map(data, 'membershipType')).map((type) => ({ text: type, value: type })),
    [data],
  )

  const columns = [
    { title: 'Row', width: '3em', fixed: 'left', dataIndex: 'id', render: (value, record, index) => <>{index + 1}</> },
    { title: 'ID', width: '3em', dataIndex: 'id', ...colProps('id') },
    {
      title: 'Registration number',
      width: '12em',
      dataIndex: 'registrationNumber',
      ...colProps('registrationNumber'),
      render: (value) => (value ? <Link to={`/admin/users?search=${encodeURIComponent(value)}`}>{value}</Link> : ''),
    },
    {
      title: 'State',
      dataIndex: 'state',
      ...colProps('state'),
      filters: stateFilters,
      onFilter: (value, record) => record.state === value,
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      ...colProps('createdAt'),
      render: (text, record) => defaultDateTime(text),
    },
    { title: 'Last name', dataIndex: 'lastName', ...colProps('lastName') },
    { title: 'Email', dataIndex: 'email', ...colProps('email') },
    {
      title: 'Membership type',
      dataIndex: 'membershipType',
      ...colProps('membershipType'),
      filters: membershipTypeFilters,
      onFilter: (value, record) => record.membershipType === value,
    },
    { title: 'Phone', dataIndex: 'phone', ...colProps('phone') },
    { title: 'Postal code', dataIndex: 'postalCode', ...colProps('postalCode') },
    { title: 'Price', dataIndex: 'price', ...colProps('price') },
    { title: 'Street', dataIndex: 'street', ...colProps('street') },
  ]

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setFilteredData(extra.currentDataSource)
  }

  return (
    // <div style={{ overflowx: 'scroll' }}>
    <div>
      <Section title="Registrations">
        <b>Possible "State" values:</b>
        <ul style={{ fontSize: '10pt', paddingLeft: '2em' }}>
          <li>
            <i>"active"</i> is a normal, active registration
          </li>
          <li>
            <i>"inactive"</i> means a registration was active, but is no longer active (e.g. we refunded their
            membership fees or they were kicked out of the club)
          </li>
          <li>
            <i>"payment_pending"</i> means they used the link to avoid PayPal payment, and we're currently waiting on an
            e-transfer, cash, or cheque. The state is manually updated to "active" when we've confirmed payment. This is
            also the state of registrations for directors/coordinators until they're manually changed to "active"
            (without payment)
          </li>
          <li>
            <i>"payment_in_progress"</i> means they got to the final step of registration but did not complete payment
            via PayPal. These get manually removed after a few days.
          </li>
        </ul>
        <div ref={tableWrapperRef}>
          <Table
            className="compactTable"
            scroll={{ x: 2000, y: 600 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            loading={loading}
            pagination={false}
            exportable
            exportableProps={{ showColumnPicker: true, btnProps: { type: 'link' }, dataSource: filteredData }}
            searchable
            onChange={handleTableChange}
          />
        </div>
      </Section>
    </div>
  )
}

export default RegistrationListPage
